import { createFileRoute } from "@tanstack/react-router";
import { editorClient } from "@/lib/editorClient";
import { defaultPageMeta, usePublished } from "@op/utils";
import type { Permissions } from "@measured/puck";
import type {
  Data,
  ContentComponentConfig,
  ContentRootProps,
} from "@op/puck-types";
import { makeContentPuckConfig } from "@op/puck";
import { useCallback, useMemo } from "react";
import { Editor } from "@/components/app/Editor";
import { useEnable } from "@/hooks/useEnable";
import { useQueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";

async function fetchContent(versionId: string) {
  const res = await editorClient.versions[":versionId"].content.$get({
    param: {
      versionId,
    },
  });
  if (!res.ok) {
    if (res.status === 401) throw redirect({ to: "/login" });
    throw new Error("Failed to get content");
  }
  const data = await res.json();
  return data;
}

export const Route = createFileRoute(
  "/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId/content/"
)({
  component: RouteComponent,
  loader: async ({ params }) => await fetchContent(params.versionId),
  shouldReload: () => true,
});

function RouteComponent() {
  const { versionId, organizationSlug, projectSlug } = Route.useParams();
  const {
    id,
    data: content,
    citations,
    glossaryItems,
    excursions,
    pageMeta,
  } = Route.useLoaderData();
  const published = usePublished();

  const editorPermissions = useEnable(organizationSlug, projectSlug, [
    "admin",
    "publisher",
    "editor",
  ]);

  const canDelete = useMemo(() => {
    return published ? false : editorPermissions;
  }, [editorPermissions, published]);

  const canDrag = useMemo(() => {
    return published ? false : editorPermissions;
  }, [editorPermissions, published]);

  const canDuplicate = useMemo(() => {
    return published ? false : editorPermissions;
  }, [editorPermissions, published]);

  const canEdit = useMemo(() => {
    return published ? false : editorPermissions;
  }, [editorPermissions, published]);

  const canInsert = useMemo(() => {
    return published ? false : editorPermissions;
  }, [editorPermissions, published]);

  const canSave = useMemo(() => {
    return published ? false : editorPermissions;
  }, [editorPermissions, published]);

  const permissions: Partial<Permissions> = useMemo(
    () => ({
      delete: canDelete,
      drag: canDrag,
      duplicate: canDuplicate,
      edit: canEdit,
      insert: canInsert,
    }),
    [canDelete, canDrag, canDuplicate, canEdit, canInsert]
  );

  const queryClient = useQueryClient();

  const onSave = useCallback(
    async (data: Data<ContentComponentConfig, ContentRootProps>) => {
      if (!canSave || !content) return;
      const res = await editorClient.contents[":contentId"].$put({
        param: {
          contentId: id,
        },
        json: {
          data,
        },
      });
      if (!res.ok) {
        throw new Error("Failed to save content");
      }
      queryClient.invalidateQueries({
        queryKey: ["editor-layout", { versionId }],
      });
    },
    [id, content, canSave, queryClient]
  );

  const puckConfig = useMemo(
    () => makeContentPuckConfig(editorClient, versionId),
    [versionId]
  );

  if (!content) return null;
  return (
    <Editor
      initialData={content as Data<ContentComponentConfig, ContentRootProps>}
      onSave={onSave}
      puckConfig={puckConfig}
      permissions={permissions}
      citations={{ content: citations, excursions: {} }}
      glossaryItems={glossaryItems}
      excursions={excursions}
      organizationSlug={organizationSlug}
      projectSlug={projectSlug}
      pageMeta={{
        ...defaultPageMeta,
        ...pageMeta,
      }}
    />
  );
}
